export type LogLevel = "info" | "error" | "warning";

export type UnifiedLogEntry = {
  position: number;
  type: LogLevel;
  data: string;
  timestamp?: string;
};

// API response format for historical logs
type HistoricalLogApiEntry = {
  timestamp: string;
  line: string;
};

// Extract log level from line content
export const extractLogLevel = (line: string): LogLevel => {
  const upperLine = line.toUpperCase();
  if (upperLine.includes("ERROR") || upperLine.includes("ERR")) {
    return "error";
  }
  if (upperLine.includes("WARN") || upperLine.includes("WARNING")) {
    return "warning";
  }
  return "info";
};

// Transform historical API logs to unified format (preserve API order)
export const transformHistoricalLogs = (
  apiLogs: HistoricalLogApiEntry[]
): UnifiedLogEntry[] =>
  apiLogs.map((log, index) => ({
    position: index,
    type: extractLogLevel(log.line),
    data: log.line,
    timestamp: log.timestamp,
  }));

// SSE event format (from streaming logs)
export type SSEEvent = {
  position: number;
  type: LogLevel;
  data: string;
};

// SSE API response format (JSON in data: line)
type SSEApiLogEntry = {
  Timestamp: string;
  Line: string;
};

// Transform SSE events to unified format (already in correct format, just add timestamp field)
export const transformSSELogs = (events: SSEEvent[]): UnifiedLogEntry[] => {
  return events.map((event) => ({
    position: event.position,
    type: event.type,
    data: event.data,
    timestamp: undefined, // SSE events don't have timestamps
  }));
};

// Parse SSE data line (JSON format)
export const parseSSEDataLine = (
  dataLine: string
): { line: string; timestamp: string } | null => {
  try {
    const parsed: SSEApiLogEntry = JSON.parse(dataLine);
    return {
      line: parsed.Line,
      timestamp: parsed.Timestamp,
    };
  } catch {
    // If parsing fails, treat as plain text (backward compatibility)
    return {
      line: dataLine,
      timestamp: "",
    };
  }
};
